// TMOS Activity Tracking Hook
// Date: 2025-11-17
// Purpose: Automatic page view and error tracking

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/state/auth/AuthProvider';
import { activityService } from '@/services/activityService';

/**
 * Hook to automatically track page views and errors
 * 
 * Features:
 * - Tracks page views with time spent
 * - Tracks errors (flag only)
 * - Uses session ID for user journey tracking
 * - Non-blocking (doesn't affect user experience)
 */
export const useActivityTracking = () => {
  const location = useLocation();
  const { user } = useAuth();
  const pageEnterTimeRef = useRef<number>(Date.now());
  const sessionIdRef = useRef<string | null>(null);
  const previousPathRef = useRef<string>('');

  // Initialize session ID on mount
  useEffect(() => {
    if (!sessionIdRef.current) {
      // Try to get session ID from localStorage or generate new one
      const storedSessionId = localStorage.getItem('tmos_session_id');
      if (storedSessionId) {
        sessionIdRef.current = storedSessionId;
      } else {
        const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('tmos_session_id', newSessionId);
        sessionIdRef.current = newSessionId;
      }
    }
  }, []);

  // Track page view on route change
  useEffect(() => {
    // Skip if user is not authenticated
    if (!user || !user.id || !user.tenantId) {
      return;
    }

    const currentPath = location.pathname;
    const pageName = getPageName(currentPath);

    // Track previous page exit (if we have a previous path)
    if (previousPathRef.current && previousPathRef.current !== currentPath) {
      const timeSpent = Math.floor((Date.now() - pageEnterTimeRef.current) / 1000);
      
      // Log previous page view (async, non-blocking)
      activityService.logPageView(
        previousPathRef.current,
        getPageName(previousPathRef.current),
        timeSpent,
        sessionIdRef.current || undefined
      ).catch(() => {
        // Silently ignore errors - activity logging is non-critical
      });
    }

    // Update refs for current page
    previousPathRef.current = currentPath;
    pageEnterTimeRef.current = Date.now();

    // Log current page entry (async, non-blocking)
    activityService.logPageView(
      currentPath,
      pageName,
      undefined, // Time spent will be calculated on next page change
      sessionIdRef.current || undefined
    ).catch(() => {
      // Silently ignore errors - activity logging is non-critical
    });

    // Cleanup: Track page exit when component unmounts
    return () => {
      // Double-check user is still authenticated before logging (user might have logged out)
      if (!user || !user.id || !user.tenantId) {
        return;
      }
      
      const timeSpent = Math.floor((Date.now() - pageEnterTimeRef.current) / 1000);
      if (timeSpent > 0) {
        activityService.logPageView(
          currentPath,
          pageName,
          timeSpent,
          sessionIdRef.current || undefined
        ).catch(() => {
          // Silently ignore errors - activity logging is non-critical
        });
      }
    };
  }, [location.pathname, user?.id, user?.tenantId]);

  // Global error handler for tracking errors
  useEffect(() => {
    if (!user || !user.id || !user.tenantId) {
      return;
    }

    const handleError = (_event: ErrorEvent) => {
      // Log error occurrence (flag only, no error details)
      activityService.logError(
        location.pathname,
        sessionIdRef.current || undefined
      ).catch(() => {
        // Silently ignore errors - activity logging is non-critical
      });
    };

    const handleUnhandledRejection = (_event: PromiseRejectionEvent) => {
      // Log error occurrence (flag only)
      activityService.logError(
        location.pathname,
        sessionIdRef.current || undefined
      ).catch(() => {
        // Silently ignore errors - activity logging is non-critical
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [location.pathname, user?.id, user?.tenantId]);
};

/**
 * Get human-readable page name from path
 */
function getPageName(path: string): string {
  // Remove leading slash and split by /
  const parts = path.split('/').filter(p => p);
  
  if (parts.length === 0) {
    return 'Home';
  }

  // Capitalize first letter and join with spaces
  return parts
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' '))
    .join(' > ');
}

